import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { User, Mail, Shield, Eye, EyeOff, X, Plus, Edit, Trash2, CreditCard } from 'lucide-react';
import { getAdmins, fetchRegisterAdmin, updateAdmin, deleteAdmin } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const MainContainer = styled.div`
  max-width: 700px;
  margin: 10px auto;
  font-family: 'Roboto', 'Segoe UI', Arial, sans-serif;
  padding: 10px 0;

  @media (max-width: 1024px) {
    max-width: 95%;
    padding: 15px;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 12px;
    margin: 5px auto;
  }

  @media (max-width: 480px) {
    padding: 8px;
    margin: 0 auto;
  }
`;

const Title = styled.h2`
  color: #2c3e50;
  text-align: center;
  margin-bottom: 15px;
  font-weight: 600;
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 8px;
  font-size: 18px;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 20px;
    padding-bottom: 10px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 15px;
    padding-bottom: 8px;
  }
`;

const FormContainer = styled.form`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
  padding: 15px;
  margin-bottom: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 20px;
    margin-bottom: 25px;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 15px;
    margin-bottom: 20px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 12px;
  
  label {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
    font-weight: 500;
    color: #34495e;
    font-size: 12px;
  }
  
  input, select {
    width: 100%;
    padding: 8px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    font-size: 12px;
    transition: all 0.3s;
    box-sizing: border-box;
    
    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb, 52, 152, 219), 0.2);
    }
  }

  @media (max-width: 768px) {
    margin-bottom: 18px;

    label {
      font-size: 14px;
      margin-bottom: 8px;
      font-weight: 600;
      gap: 8px;
    }

    input, select {
      padding: 12px;
      font-size: 16px; /* Важно для iOS */
      border-radius: 6px;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 15px;

    label {
      font-size: 13px;
      margin-bottom: 6px;
      gap: 6px;
    }

    input, select {
      padding: 10px;
      font-size: 16px;
      border-radius: 5px;
    }
  }
`;

const PasswordInputContainer = styled.div`
  position: relative;
  
  input {
    padding-right: 35px;
  }
  
  button {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #7f8c8d;
    cursor: pointer;
    padding: 0;
    touch-action: manipulation;
    
    &:hover {
      color: var(--primary-color);
    }
  }

  @media (max-width: 768px) {
    input {
      padding-right: 45px;
    }

    button {
      right: 12px;
      padding: 4px;
    }
  }

  @media (max-width: 480px) {
    input {
      padding-right: 40px;
    }

    button {
      right: 10px;
      padding: 3px;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 10px;
  margin-top: 4px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 3px;

  @media (max-width: 768px) {
    font-size: 12px;
    margin-top: 6px;
    gap: 4px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    margin-top: 4px;
    gap: 3px;
  }
`;

const StatusMessage = styled.div`
  margin: 0 0 12px;
  padding: 8px;
  border-radius: 4px;
  text-align: center;
  font-weight: 500;
  font-size: 11px;
  
  &.success {
    background-color: #d4edda;
    color: #155724;
    border-left: 4px solid var(--green);
  }
  
  &.error {
    background-color: #f8d7da;
    color: #721c24;
    border-left: 4px solid #dc3545;
  }

  @media (max-width: 768px) {
    padding: 12px;
    font-size: 14px;
    margin-bottom: 18px;
    border-radius: 6px;
    border-left-width: 5px;
  }

  @media (max-width: 480px) {
    padding: 10px;
    font-size: 13px;
    margin-bottom: 15px;
    border-radius: 4px;
    border-left-width: 4px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 12px;

  @media (max-width: 768px) {
    gap: 12px;
    margin-top: 20px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 10px;
    margin-top: 15px;
  }
`;

const Button = styled.button`
  background-color: ${props => props.red ? '#e74c3c' : props.secondary ? '#7f8c8d' : 'var(--primary-color)'};
  color: white;
  padding: ${props => props.small ? '4px 8px' : '6px 12px'};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  font-size: ${props => props.small ? '10px' : '11px'};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  touch-action: manipulation;
  
  &:hover {
    background-color: ${props => props.red ? '#c0392b' : props.secondary ? '#6c7a7a' : 'var(--primary-color)'};
    transform: translateY(-1px);
    filter: brightness(0.9);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: ${props => props.small ? '8px 12px' : '12px 16px'};
    font-size: ${props => props.small ? '13px' : '14px'};
    border-radius: 6px;
    gap: 6px;
    min-height: 44px;
  }

  @media (max-width: 480px) {
    padding: ${props => props.small ? '8px 10px' : '10px 14px'};
    font-size: ${props => props.small ? '12px' : '13px'};
    min-height: 40px;
    flex: 1;

    &:hover {
      transform: none;
    }
  }
`;

const AdminsSection = styled.div`
  margin-top: 20px;

  @media (max-width: 768px) {
    margin-top: 25px;
  }

  @media (max-width: 480px) {
    margin-top: 20px;
  }
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eaeaea;
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 15px;
    padding-bottom: 8px;
  }

  @media (max-width: 480px) {
    font-size: 15px;
    margin-bottom: 12px;
    padding-bottom: 6px;
  }
`;

const AdminCard = styled.div`
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s;
  margin-bottom: 10px;
  border-left: 2px solid var(--primary-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    padding: 15px;
    margin-bottom: 15px;
    border-radius: 8px;
    border-left-width: 3px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    padding: 12px;
    margin-bottom: 12px;
    border-radius: 6px;
    border-left-width: 2px;

    &:hover {
      transform: none;
    }
  }
`;

const AdminDetails = styled.div`
  flex-grow: 1;

  @media (max-width: 480px) {
    margin-bottom: 10px;
  }
`;

const AdminName = styled.div`
  font-weight: 600;
  color: #2c3e50;
  font-size: 12px;
  margin-bottom: 3px;

  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 5px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 4px;
  }
`;

const AdminInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  color: #7f8c8d;
  font-size: 10px;
  margin-bottom: 3px;

  @media (max-width: 768px) {
    font-size: 13px;
    gap: 5px;
    margin-bottom: 5px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    gap: 4px;
    margin-bottom: 4px;
  }
`;

const AdminRole = styled.div`
  display: inline-block;
  background: var(--primary-color);
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 500;
  margin-top: 4px;

  @media (max-width: 768px) {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    margin-top: 6px;
  }

  @media (max-width: 480px) {
    padding: 3px 6px;
    border-radius: 3px;
    font-size: 11px;
    margin-top: 4px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 6px;

  @media (max-width: 768px) {
    gap: 10px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 768px) {
    width: 14px;
    height: 14px;
  }

  @media (max-width: 480px) {
    width: 13px;
    height: 13px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 20px 0;
  color: #7f8c8d;
  font-style: italic;
  font-size: 12px;

  @media (max-width: 768px) {
    padding: 30px 20px;
    font-size: 14px;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    padding: 25px 15px;
    font-size: 13px;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0 10px;
  flex-wrap: wrap;
  gap: 2px;

  @media (max-width: 768px) {
    margin: 25px 0 15px;
    gap: 4px;
  }

  @media (max-width: 480px) {
    margin: 20px 0 10px;
    gap: 2px;
  }
`;

const PageButton = styled.button`
  background-color: ${props => props.active ? 'var(--primary-color)' : '#f8f9fa'};
  color: ${props => props.active ? 'white' : '#34495e'};
  border: ${props => props.active ? 'none' : '1px solid #e0e0e0'};
  padding: 4px 8px;
  margin: 0 2px;
  border-radius: 3px;
  cursor: pointer;
  font-weight: ${props => props.active ? '600' : '400'};
  transition: all 0.2s;
  font-size: 11px;
  touch-action: manipulation;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-color)' : '#e9ecef'};
    transform: translateY(-1px);
    filter: ${props => props.active ? 'brightness(0.9)' : 'none'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 14px;
    border-radius: 5px;
    margin: 0 3px;
    min-height: 36px;
    min-width: 36px;
  }

  @media (max-width: 480px) {
    padding: 6px 10px;
    font-size: 13px;
    margin: 0 2px;
    min-height: 32px;
    min-width: 32px;

    &:hover {
      transform: none;
    }
  }
`;

// Modal styles с правильным позиционированием
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  padding: 10px;
  
  /* Добавляем отступ слева в зависимости от состояния сайдбара на десктопе */
  @media (min-width: 1025px) {
    margin-left: ${props => props.sidebarCollapsed ? '80px' : '300px'};
    transition: margin-left 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
    top: 60px;
    height: calc(100vh - 60px);
  }

  @media (max-width: 1024px) {
    margin-left: 0;
    padding: 15px;
    top: 60px;
    height: calc(100vh - 60px);
  }

  @media (max-width: 480px) {
    padding: 10px;
    top: 60px;
    height: calc(100vh - 60px);
  }
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 6px;
  padding: 15px;
  width: 100%;
  max-width: 500px;
  max-height: calc(100vh - 140px);
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 8px;
    max-height: calc(100vh - 120px);
  }

  @media (max-width: 480px) {
    padding: 15px;
    border-radius: 6px;
    max-height: calc(100vh - 100px);
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    margin-bottom: 15px;
  }
`;

const ModalTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #7f8c8d;
  cursor: pointer;
  padding: 3px;
  touch-action: manipulation;
  
  &:hover {
    color: #2c3e50;
  }

  @media (max-width: 768px) {
    padding: 5px;
  }

  @media (max-width: 480px) {
    padding: 4px;
  }
`;

const InfoCard = styled.div`
  background: var(--primary-color);
  background: linear-gradient(135deg, rgba(var(--primary-color-rgb, 52, 152, 219), 0.1) 0%, rgba(var(--primary-color-rgb, 52, 152, 219), 0.05) 100%);
  border: 1px solid rgba(var(--primary-color-rgb, 52, 152, 219), 0.2);
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    padding: 12px;
    margin-bottom: 18px;
    border-radius: 6px;
  }

  @media (max-width: 480px) {
    padding: 10px;
    margin-bottom: 15px;
    border-radius: 5px;
  }
`;

const InfoText = styled.div`
  color: var(--primary-color);
  font-size: 12px;
  
  strong {
    font-size: 13px;
    font-family: monospace;
  }
  
  .note {
    font-size: 10px;
    margin-top: 4px;
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    font-size: 14px;

    strong {
      font-size: 15px;
    }

    .note {
      font-size: 12px;
      margin-top: 6px;
    }
  }

  @media (max-width: 480px) {
    font-size: 13px;

    strong {
      font-size: 14px;
    }

    .note {
      font-size: 11px;
      margin-top: 4px;
    }
  }
`;

function AdminManagement() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const [admins, setAdmins] = useState([]);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const { showNotification } = useNotification();

  // Добавляем состояние для отслеживания сайдбара
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

  // Пагинация
  const [currentPage, setCurrentPage] = useState(1);
  const adminsPerPage = 3;

  // Загрузка администраторов
  useEffect(() => {
    loadAdmins();
  }, []);

  // Получаем состояние сайдбара из localStorage при загрузке компонента
  useEffect(() => {
    const collapsed = JSON.parse(localStorage.getItem('sidebarCollapsed') || 'true');
    setIsSidebarCollapsed(collapsed);

    // Слушаем изменения в localStorage для синхронизации состояния
    const handleStorageChange = () => {
      const collapsed = JSON.parse(localStorage.getItem('sidebarCollapsed') || 'true');
      setIsSidebarCollapsed(collapsed);
    };

    window.addEventListener('storage', handleStorageChange);

    // Также добавим кастомное событие для внутренних изменений
    const handleSidebarToggle = () => {
      const collapsed = JSON.parse(localStorage.getItem('sidebarCollapsed') || 'true');
      setIsSidebarCollapsed(collapsed);
    };

    window.addEventListener('sidebarToggled', handleSidebarToggle);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sidebarToggled', handleSidebarToggle);
    };
  }, []);

  const loadAdmins = async () => {
    try {
      const data = await getAdmins();
      setAdmins(data);
    } catch (error) {
      console.error('Ошибка загрузки администраторов:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Очищаем ошибку поля при изменении
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));

    // Очищаем ошибку поля при изменении
    if (editErrors[name]) {
      setEditErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (data, isEdit = false) => {
    const newErrors = {};

    if (!data.name.trim()) {
      newErrors.name = 'Имя и фамилия обязательны';
    }

    if (!data.email.trim()) {
      newErrors.email = 'Email обязателен';
    } else if (!/\S+@\S+\.\S+/.test(data.email)) {
      newErrors.email = 'Некорректный email';
    }

    if (!isEdit && !data.password.trim()) {
      newErrors.password = 'Пароль обязателен';
    } else if (data.password && data.password.length < 6) {
      newErrors.password = 'Пароль должен содержать минимум 6 символов';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm(formData);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setLoading(true);

    try {
      await fetchRegisterAdmin(formData);
      setFormData({ name: '', email: '', password: '' });
      await loadAdmins();
      showNotification("Администратор успешно создан", "success");
      setCurrentPage(1);
    } catch (error) {
      showNotification("Ошибка при создании администратора", "error");
      console.error('Ошибка регистрации администратора:', error);
      setErrors({ general: 'Ошибка при сохранении. Попробуйте еще раз.' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm(editFormData, true);
    setEditErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setEditLoading(true);

    try {
      const updateData = { ...editFormData };
      if (!updateData.password) {
        delete updateData.password;
      }
      await updateAdmin(editingAdmin.id, updateData);
      setEditingAdmin(null);
      setIsEditModalOpen(false);
      await loadAdmins();
      showNotification("Данные администратора обновлены", "success");
    } catch (error) {
      showNotification("Ошибка при обновлении администратора", "error");
      console.error('Ошибка обновления администратора:', error);
      setEditErrors({ general: 'Ошибка при сохранении. Попробуйте еще раз.' });
    } finally {
      setEditLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: ''
    });
    setErrors({});
  };

  const resetEditForm = () => {
    setEditFormData({
      name: '',
      email: '',
      password: ''
    });
    setEditingAdmin(null);
    setEditErrors({});
  };

  const handleEdit = (admin) => {
    setEditingAdmin(admin);
    setEditFormData({
      name: admin.name,
      email: admin.email,
      password: ''
    });
    setEditErrors({});
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    resetEditForm();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этого администратора?')) {
      try {
        await deleteAdmin(id);

        // Корректировка пагинации
        setAdmins(prevAdmins => {
          const updatedAdmins = prevAdmins.filter(admin => admin.id !== id);
          const maxPage = Math.ceil(updatedAdmins.length / adminsPerPage);
          if (currentPage > maxPage && currentPage > 1) {
            setCurrentPage(maxPage);
          }
          return updatedAdmins;
        });
        showNotification("Администратор успешно удален", "success");
      } catch (error) {
        showNotification("Ошибка при удалении администратора", "error");
        console.error('Ошибка удаления администратора:', error);
      }
    }
  };

  // Пагинация
  const indexOfLastAdmin = currentPage * adminsPerPage;
  const indexOfFirstAdmin = indexOfLastAdmin - adminsPerPage;
  const currentAdmins = admins.slice(indexOfFirstAdmin, indexOfLastAdmin);
  const totalPages = Math.ceil(admins.length / adminsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <MainContainer>
      <Title>Управление администраторами</Title>

      {/* Форма добавления администратора */}
      <FormContainer onSubmit={handleSubmit}>

        {errors.general && (
          <div style={{ color: '#e74c3c', marginBottom: '10px', fontSize: '12px' }}>
            {errors.general}
          </div>
        )}

        <FormGroup>
          <label>
            <User size={14} />
            Имя и фамилия *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Введите имя и фамилию"
            disabled={loading}
          />
          {errors.name && <ErrorMessage><span>⚠️</span>{errors.name}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <label>
            <Mail size={14} />
            Email *
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Введите email"
            disabled={loading}
          />
          {errors.email && <ErrorMessage><span>⚠️</span>{errors.email}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <label>
            Пароль *
          </label>
          <PasswordInputContainer>
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Введите пароль"
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </PasswordInputContainer>
          {errors.password && <ErrorMessage><span>⚠️</span>{errors.password}</ErrorMessage>}
        </FormGroup>

        <ButtonGroup>
          <Button
            type="submit"
            disabled={loading}
          >
            Добавить администратора
            {loading && <LoadingSpinner />}
          </Button>
        </ButtonGroup>
      </FormContainer>

      {/* Список администраторов */}
      <AdminsSection data-list-section>
        <SectionTitle>Список администраторов ({admins.length})</SectionTitle>

        {admins.length > 0 ? (
          <>
            {currentAdmins.map((admin) => (
              <AdminCard key={admin.id}>
                <AdminDetails>
                  <AdminName>{admin.name}</AdminName>
                  <AdminInfo>
                    <Mail size={10} />
                    {admin.email}
                  </AdminInfo>
                  <AdminInfo>
                    <CreditCard size={10} />
                    Читательский билет: {admin.libraryCard}
                  </AdminInfo>
                  <AdminRole>
                    <Shield size={10} style={{ marginRight: '3px' }} />
                    {admin.role}
                  </AdminRole>
                </AdminDetails>

                <ActionButtons>
                  <Button
                    small
                    onClick={() => handleEdit(admin)}
                    disabled={loading || editLoading}
                  >
                    <Edit size={12} />
                    Редактировать
                  </Button>
                  <Button
                    small
                    onClick={() => handleDelete(admin.id)}
                    disabled={loading || editLoading}
                    red
                  >
                    <Trash2 size={12} />
                    Удалить
                  </Button>
                </ActionButtons>
              </AdminCard>
            ))}

            {/* Пагинация */}
            {totalPages > 1 && (
              <PaginationContainer>
                <PageButton
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  &laquo;
                </PageButton>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                  <PageButton
                    key={number}
                    active={currentPage === number}
                    onClick={() => paginate(number)}
                  >
                    {number}
                  </PageButton>
                ))}

                <PageButton
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  &raquo;
                </PageButton>
              </PaginationContainer>
            )}
          </>
        ) : (
          <EmptyState>
            Администраторов пока нет. Добавьте первого администратора, используя форму выше.
          </EmptyState>
        )}
      </AdminsSection>

      {/* Модальное окно редактирования администратора */}
      {isEditModalOpen && (
        <ModalOverlay sidebarCollapsed={isSidebarCollapsed}>
          <ModalContainer>
            <ModalHeader>
              <ModalTitle>Редактирование администратора</ModalTitle>
              <CloseButton onClick={closeEditModal}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            {editingAdmin && (
              <InfoCard>
                <InfoText>
                  <strong>Читательский билет: {editingAdmin.libraryCard}</strong>
                  <div className="note">Читательский билет не подлежит изменению</div>
                </InfoText>
              </InfoCard>
            )}

            <form onSubmit={handleEditSubmit}>
              {editErrors.general && (
                <StatusMessage className="error">
                  {editErrors.general}
                </StatusMessage>
              )}

              <FormGroup>
                <label>
                  <User size={14} />
                  Имя и фамилия *
                </label>
                <input
                  type="text"
                  name="name"
                  value={editFormData.name}
                  onChange={handleEditChange}
                  placeholder="Введите имя и фамилию"
                  disabled={editLoading}
                />
                {editErrors.name && <ErrorMessage><span>⚠️</span>{editErrors.name}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <label>
                  <Mail size={14} />
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={editFormData.email}
                  onChange={handleEditChange}
                  placeholder="Введите email"
                  disabled={editLoading}
                />
                {editErrors.email && <ErrorMessage><span>⚠️</span>{editErrors.email}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <label>
                  Новый пароль
                </label>
                <PasswordInputContainer>
                  <input
                    type={showEditPassword ? 'text' : 'password'}
                    name="password"
                    value={editFormData.password}
                    onChange={handleEditChange}
                    placeholder="Оставьте пустым для сохранения текущего"
                    disabled={editLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowEditPassword(!showEditPassword)}
                  >
                    {showEditPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </PasswordInputContainer>
                {editErrors.password && <ErrorMessage><span>⚠️</span>{editErrors.password}</ErrorMessage>}
              </FormGroup>

              <ButtonGroup>
                <Button type="submit" disabled={editLoading}>
                  {editLoading ? 'Сохранение...' : 'Сохранить изменения'}
                  {editLoading && <LoadingSpinner />}
                </Button>
                <Button
                  type="button"
                  onClick={closeEditModal}
                  disabled={editLoading}
                  secondary
                >
                  Отмена
                </Button>
              </ButtonGroup>
            </form>
          </ModalContainer>
        </ModalOverlay>
      )}
    </MainContainer>
  );
}

export default AdminManagement;